/**
 * Utility functions for consistent property status checking across all search services
 */

export type ListingStatus = 'forSale' | 'forRent' | 'sold';

/**
 * Extract the raw status from a property data object
 * @param propertyData - Property data object from API response
 * @returns Raw status string
 */
export function extractPropertyStatus(propertyData: any): string {
  return (propertyData.homeStatus || propertyData.status || propertyData.listing?.marketingStatus || '').toString();
}

/**
 * Check if a property matches the expected listing status
 * @param propertyData - Property data object from API response
 * @param expectedStatus - Expected listing status
 * @returns True if property matches expected status
 */
export function matchesListingStatus(propertyData: any, expectedStatus: ListingStatus): boolean {
  const rawStatus = extractPropertyStatus(propertyData);
  const statusUpper = rawStatus.toUpperCase();
  const statusLower = rawStatus.toLowerCase();
  
  let matches = false;
  
  // Special handling for rent and sold searches - be more permissive
  if (expectedStatus === 'forRent') {
    // Allow properties with undefined/empty status
    if (!rawStatus || rawStatus === 'undefined' || rawStatus === '') {
      matches = true;
    }
    // Allow properties that explicitly contain RENT
    else if (statusUpper.includes('RENT') || statusLower === 'forrent') {
      matches = true;
    }
    // For rent searches, also allow properties that don't explicitly say "FOR_SALE"
    // This handles cases where the API returns mixed statuses
    else if (!statusUpper.includes('SALE') && !statusUpper.includes('SOLD')) {
      matches = true;
    }
    // Explicitly exclude only properties that are clearly for sale or sold
    else {
      matches = false;
    }
  } else if (expectedStatus === 'sold') {
    // Allow properties with undefined/empty status
    if (!rawStatus || rawStatus === 'undefined' || rawStatus === '') {
      matches = true;
    }
    // Allow properties that explicitly contain SOLD
    else if (statusLower === 'sold' || statusUpper.includes('SOLD')) {
      matches = true;
    }
    // For sold searches, also allow properties that don't explicitly say "FOR_SALE" or "RENT"
    // This handles cases where the API returns mixed statuses
    else if (!statusUpper.includes('SALE') && !statusUpper.includes('RENT')) {
      matches = true;
    }
    // Explicitly exclude only properties that are clearly for sale or rent
    else {
      matches = false;
    }
  } else {
    // For buy searches, use strict status checking
    switch (expectedStatus) {
      case 'forSale':
        matches = statusLower === 'forsale' || statusUpper.includes('SALE');
        break;
      default:
        matches = false;
    }
  }
  
  
  return matches;
}

/**
 * Filter an array of properties by listing status
 * @param properties - Array of property data objects
 * @param expectedStatus - Expected listing status
 * @returns Filtered array of properties that match the expected status
 */
export function filterPropertiesByStatus(properties: any[], expectedStatus: ListingStatus): any[] {
  return properties.filter(property => matchesListingStatus(property, expectedStatus));
}

/**
 * Check if a property should be excluded from a specific search flow
 * @param propertyData - Property data object from API response
 * @param searchFlow - The search flow context ('buy', 'rent', 'sold')
 * @returns True if property should be excluded from this flow
 */
export function shouldExcludeFromFlow(propertyData: any, searchFlow: 'buy' | 'rent' | 'sold'): boolean {
  const rawStatus = extractPropertyStatus(propertyData);
  const statusUpper = rawStatus.toUpperCase();
  
  switch (searchFlow) {
    case 'buy':
      // Exclude rentals and sold properties from buy flow
      return statusUpper.includes('RENT') || statusUpper.includes('SOLD');
    case 'rent':
      // Exclude for-sale and sold properties from rent flow
      return statusUpper === 'FOR_SALE' || statusUpper.includes('SOLD');
    case 'sold':
      // Exclude for-sale and rental properties from sold flow
      return statusUpper === 'FOR_SALE' || statusUpper.includes('RENT') || statusUpper === 'COMING_SOON';
    default:
      return false;
  }
}

/**
 * Get a human-readable status description
 * @param propertyData - Property data object from API response
 * @returns Human-readable status string
 */
export function getStatusDescription(propertyData: any): string {
  const rawStatus = extractPropertyStatus(propertyData);
  const statusUpper = rawStatus.toUpperCase();
  
  if (statusUpper.includes('SALE')) return 'For Sale';
  if (statusUpper.includes('RENT')) return 'For Rent';
  if (statusUpper.includes('SOLD')) return 'Sold';
  if (statusUpper === 'COMING_SOON') return 'Coming Soon';
  
  return rawStatus || 'Unknown';
}
